import React from "react";
import { match } from "react-router-dom";
import { History, Location } from "history";
import TagsTables from "@/components/react/TagsTables";
import {
    mapDataToCategories,
    mapDataToCustomTags,
    Columns,
    updateTagsConfig,
    onChangeUpdateTags,
    type TagsConfig,
    type TagUpdateValue,
} from "@/components/react/ReactTableTags";
import EntityEditTemplate from "../../../shared/EntityEditTemplate";
import { MATERIAL_PLANT_TAGS } from "@/lib/ManufacturingTagsEditor.generated";
import { MATERIAL_PLANT_TAGS_ACCESS } from "@/lib/ManufacturingTags.generated";
import { getBackend as getBackend2 } from "@/lib/backend/Backend2";
import { translate } from "@/components/IntlProviderWrapper";
import type { IUpdateMaterialPlantReq } from "@/lib/backend/manufacturing2.generated.types";
import type { ITags } from "@/lib/Models";
import * as t from "@/lib/backend/manufacturing2.generated.types";

import ErrorComponent from "@/components/ErrorComponent";

type Props = {
    match: match<{ material_uuid?: string; plant_uuid?: string }>;
    history: History;
    location: Location;
    plant_title: string;
    material_title: string;
};

type State = {
    tags: ITags;
    title: string;
    error: string | null;
    plant: t.IPlantData | null;
    material: t.IMaterialModel | null;
};

class MaterialPlantEdit extends React.Component<Props, State> {
    state: State = {
        error: null,
        tags: {},
        title: "",
        plant: null,
        material: null,
    };

    componentDidMount() {
        this.load();
    }

    getMaterialUuid = () => {
        return this.props.match.params.material_uuid;
    };

    getPlantUuid = () => {
        return this.props.match.params.plant_uuid;
    };

    load = async () => {
        try {
            this.setState({ error: null });

            const res_material_plant_tags = await getBackend2().manufacturing.getMaterialPlantTags({
                uuid: this.getMaterialUuid(),
                plant_uuid: this.getPlantUuid(),
            });

            const material_plant_tags = res_material_plant_tags.data.find(
                material_plant_tag => material_plant_tag.material_uuid === this.getMaterialUuid()
            );

            const res_plant = await getBackend2().manufacturing.getPlants({});
            const plant = res_plant.plants.find(p => p.uuid === this.getPlantUuid());

            const res_material = await getBackend2().manufacturing.searchMaterials({
                material_uuid: this.getMaterialUuid(),
            });

            const material = res_material.data.find(m => m.uuid === this.getMaterialUuid());

            this.setState({
                tags: material_plant_tags ? material_plant_tags.tags : {},
                plant: plant ?? null,
                material: material ?? null,
            });
        } catch (e) {
            this.setState({
                error: translate("common.error", "An error occured while fetching data."),
            });
        }
    };

    onChangeCell = (name: string) => (value: TagUpdateValue) => {
        this.setState({ tags: onChangeUpdateTags(this.state.tags, name, value) });
    };

    renderError = () => {
        if (this.state.error) {
            return <ErrorComponent msg={this.state.error} type="error" />;
        }
        return null;
    };

    onBack = () => {
        this.props.history.goBack();
    };

    onSave = async () => {
        try {
            const material_uuid = this.getMaterialUuid();
            const plant_uuid = this.getPlantUuid();

            if (material_uuid && plant_uuid) {
                const material_params: IUpdateMaterialPlantReq = {
                    material_uuid,
                    plant_uuid,
                    tags: this.state.tags,
                };

                await getBackend2().manufacturing.updateMaterialPlant(material_params);
                this.props.history.goBack();
            }
        } catch (e: unknown) {
            console.log(e);
            this.setState({
                error: translate("common.error_save", "An error occured while saving data."),
            });
        }
    };

    getTitle = () => {
        return (
            <div>
                <span>
                    {translate("common.plant", "Plant")}: {this.state.plant ? this.state.plant.title : "?"}
                </span>
                <br />
                <span>
                    {translate("common.material", "Material")}: {this.state.material ? this.state.material.title : "?"}
                </span>
            </div>
        );
    };
    render() {
        const tagsConfig = updateTagsConfig(MATERIAL_PLANT_TAGS as TagsConfig);
        const tagCategories = mapDataToCategories(this.state.tags, tagsConfig, MATERIAL_PLANT_TAGS_ACCESS);
        return (
            <EntityEditTemplate onSave={this.onSave} title={this.getTitle()} onBack={this.onBack}>
                {this.renderError()}
                <TagsTables
                    categories={tagCategories}
                    columns={Columns}
                    onChangeCell={this.onChangeCell}
                    customTags={mapDataToCustomTags(this.state.tags, tagsConfig)}
                />
            </EntityEditTemplate>
        );
    }
}

export default MaterialPlantEdit;
